import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, X, Send, User, AtSign, Phone, Paperclip, File } from 'lucide-react';
import { toast } from 'sonner';

export const NewMessageModal = ({ onClose, onMessageSent }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 10 MB)');
        return;
      }
      setAttachment(file);
      toast.success(`📎 Fichier "${file.name}" ajouté`);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    toast.info('Fichier retiré');
  };

  const handleSend = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('Le nom du client est obligatoire');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('L\'email du client est obligatoire');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Le sujet est obligatoire');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Le message est obligatoire');
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Email invalide');
      return;
    }

    setSending(true);

    try {
      const apiUrl = window.location.origin;
      const token = localStorage.getItem('admin_token');

      // Créer FormData pour envoyer le fichier
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);
      if (attachment) {
        formDataToSend.append('attachment', attachment);
      }

      const response = await fetch(`${apiUrl}/api/send-custom-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du message');
      }

      toast.success('✅ Message envoyé avec succès !');
      onMessageSent();
      onClose();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('❌ Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-t-2xl flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Mail size={24} />
            <div>
              <h2 className="text-2xl font-bold">Nouveau message</h2>
              <p className="text-purple-100 text-sm">Envoyer un email à un client</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-purple-800 p-2 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-5">
          {/* Client Info */}
          <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
            <p className="text-sm font-semibold text-blue-900 mb-3">👤 Informations du client</p>
            
            <div className="space-y-3">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-sm font-medium mb-1 flex items-center space-x-1">
                  <User size={14} />
                  <span>Nom du client *</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Ex: Jean Dupont"
                  className="text-base"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium mb-1 flex items-center space-x-1">
                  <AtSign size={14} />
                  <span>Email *</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Ex: jean.dupont@email.com"
                  className="text-base"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-sm font-medium mb-1 flex items-center space-x-1">
                  <Phone size={14} />
                  <span>Téléphone (optionnel)</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Ex: 06 12 34 56 78"
                  className="text-base"
                />
              </div>
            </div>
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="subject" className="text-lg font-semibold mb-2">Sujet *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="Objet du message"
              className="text-base"
            />
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message" className="text-lg font-semibold mb-2">Message *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Votre message..."
              rows={10}
              className="text-base"
            />
          </div>

          {/* File Attachment */}
          <div>
            <Label className="text-lg font-semibold mb-2">Pièce jointe (optionnel)</Label>
            <div className="mt-2">
              {!attachment ? (
                <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors">
                  <Paperclip size={20} className="mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600">Joindre un fichier (PDF, facture, rapport...) - Max 10 MB</span>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.crash,.panic"
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <File size={20} className="text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-900">{attachment.name}</p>
                      <p className="text-xs text-green-700">{(attachment.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeAttachment}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 rounded-b-2xl flex justify-end space-x-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="px-6"
            disabled={sending}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSend}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 flex items-center space-x-2"
            disabled={sending}
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Envoi...</span>
              </>
            ) : (
              <>
                <Send size={18} />
                <span>Envoyer</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
